import {
  Box,
  BoxModel,
  createShapeId,
  Editor,
  TLShape,
  TLShapeId,
  VecModel,
} from "tldraw";
import { FocusedFill, FocusedFillSchema } from "../shared/format/FocusedFill";
import { SimpleShapeId } from "../shared/types/ids-schema";
import { TldrawAgent } from "./agent/TldrawAgent";
import {
  StudentInkCluster,
  computeStudentInkClusters,
} from "./ink/studentInkClusters";

/**
 * This class contains handles the transformations that happen throughout a
 * request. It contains helpers that can be used to change prompt parts
 * before they get sent to the model, as well as helpers that can be used to
 * change incoming actions as they get streamed back from the model.
 *
 * Many transformation methods save some state. For example, the
 * `ensureShapeIdIsUnique` method changes a shape's ID if it's not unique, and
 * it saves a record of this change so that further actions can continue to
 * refer to the shape by its untransformed ID.
 */
export class AgentHelpers {
  /**
   * The agent that the this intance of AgentHelpers is for.
   */
  agent: TldrawAgent;

  /**
   * The editor that the this intance of AgentHelpers is for.
   */
  editor: Editor;

  /** This turn's handwriting, computed on first use. See below. */
  private inkClusters: StudentInkCluster[] | null = null;
  private inkBounds: BoxModel | null = null;
  private studentEdits = new Map<TLShapeId, string>();

  constructor(agent: TldrawAgent) {
    this.agent = agent;
    this.editor = agent.editor;
  }

  getStudentEdits(shapes: TLShape[]): Map<TLShapeId, string> {
    this.studentEdits = this.agent.requests.getStudentEdits(shapes);
    return this.studentEdits;
  }

  acknowledgeStudentEdits(): void {
    this.agent.requests.acknowledgeStudentEdits(this.studentEdits);
  }

  /**
   * The student's handwriting as of the start of this turn.
   *
   * Computed ONCE and shared by every part that reports ink. The parts run
   * serially with the screenshot render between them, so recomputing would read
   * a live editor at different moments — and since the clusters carry `changed`,
   * a stroke landing mid-render would re-baseline and drop the earlier change
   * for good. One read, one answer, all three parts agreeing.
   */
  getStudentInkClusters(bounds: BoxModel): StudentInkCluster[] {
    if (this.inkClusters) {
      // One board per turn is the invariant, so a second region is not served —
      // recomputing would re-baseline mid-assembly, which is the bug this
      // exists to prevent. Loud, because the wrong clusters would be silent.
      if (
        this.inkBounds!.x !== bounds.x ||
        this.inkBounds!.y !== bounds.y ||
        this.inkBounds!.w !== bounds.w ||
        this.inkBounds!.h !== bounds.h
      ) {
        console.warn(
          "[AgentHelpers] ink clusters already computed for different bounds; returning the first",
          { first: this.inkBounds, requested: bounds },
        );
      }
      return this.inkClusters;
    }
    this.inkBounds = bounds;
    this.inkClusters = computeStudentInkClusters(
      this.editor,
      this.agent,
      Box.From(bounds),
    );
    return this.inkClusters;
  }

  /**
   * A map of shape ids that have been transformed as part of this request.
   * The key is the original id, and the value is the transformed id.
   */
  shapeIdMap = new Map<string, string>();

  /**
   * Ensure that a shape ID is unique.
   * @param id - The id to check (should be a SimpleShapeId from the model, not a TLShapeId).
   * @returns The unique id (always without the "shape:" prefix).
   */
  ensureShapeIdIsUnique(id = "shape" as SimpleShapeId): SimpleShapeId {
    // todo: remove default and have a better handling of cases where id is undefined

    const { editor } = this.agent;
    // Defensively strip the prefix in case the model incorrectly includes it

    // Ensure the id is unique by incrementing a number at the end
    let newId = id;
    let existingShape = editor.getShape(`shape:${newId}` as TLShapeId);
    while (existingShape) {
      newId = /^.*(\d+)$/.exec(newId)?.[1]
        ? (newId.replace(/(\d+)$/, (m) => {
            return (+m + 1).toString();
          }) as SimpleShapeId)
        : (`${newId}-1` as SimpleShapeId);
      existingShape = editor.getShape(`shape:${newId}` as TLShapeId);
    }

    // If the id was transformed, track it so that future events can refer to it by its original id.
    if (id !== newId) {
      this.shapeIdMap.set(id, newId);
    }

    return newId as SimpleShapeId;
  }

  /**
   * Ensure that a shape ID refers to a real shape.
   * @param id - The id to check.
   * @returns The real id, or null if the shape doesn't exist.
   */
  ensureShapeIdExists(id: SimpleShapeId): SimpleShapeId | null {
    const { editor } = this.agent;

    // If there's already a transformed ID, use that
    const existingId = this.shapeIdMap.get(id);
    if (existingId) {
      return existingId as SimpleShapeId;
    }

    // If there's an existing shape with this ID, use that
    const existingShape = editor.getShape(createShapeId(id));
    if (existingShape) {
      return id;
    }

    // Otherwise, give up
    return null;
  }

  /**
   * Ensure that a value is a number.
   * Used for checking incoming data from the model.
   * @returns The number, or null if the value is not a number.
   */
  ensureValueIsNumber(value: any): number | null {
    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string") {
      const parsedValue = parseFloat(value);
      if (isNaN(parsedValue)) {
        return null;
      }
      return parsedValue;
    }

    return null;
  }

  /**
   * Ensure that a value is a vector.
   * Used for checking incoming data from the model.
   * @returns The vector, or null if the value is not a vector.
   */
  ensureValueIsVec(value: any): VecModel | null {
    if (!value) return null;
    if (typeof value !== "object") return null;
    if (!("x" in value) || !("y" in value)) return null;

    const x = this.ensureValueIsNumber(value.x);
    const y = this.ensureValueIsNumber(value.y);
    if (x === null || y === null) return null;
    return { x, y };
  }

  /**
   * Ensure that a value is a boolean.
   * Used for checking incoming data from the model.
   * @returns The boolean, or null if the value is not a boolean.
   */
  ensureValueIsBoolean(value: any): boolean | null {
    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "number") {
      return value > 0;
    }

    if (typeof value === "string") {
      return value !== "false";
    }

    return null;
  }

  /**
   * Ensure that a value is a focused fill.
   * Used for checking incoming data from the model.
   * @returns The focused fill, or null if the value is not a focused fill.
   */
  ensureValueIsFocusedFill(value: any): FocusedFill | null {
    const focusedFill = FocusedFillSchema.safeParse(value);
    if (focusedFill.success) {
      return focusedFill.data;
    }
    return null;
  }

  /** Rounds into a copy: inputs may be live editor boxes or cached clusters. */
  roundBox(boxModel: BoxModel): BoxModel {
    return {
      x: Math.round(boxModel.x),
      y: Math.round(boxModel.y),
      w: Math.round(boxModel.w),
      h: Math.round(boxModel.h),
    };
  }
}
